import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import {EyeTrackerService} from 'projects/eye-tracker/src/lib/eye-tracker.service';
import {Subscription} from 'rxjs';

export interface IErrorConfig {
    title: string;
    text: string;
    reload: string;
    tryLater: string;
}

export interface ICalibConfig {
    calibrationText: string;
    sec: string;
    eyeTrackingTestText: string;
    min: string;
    conventionalTestText: string;
    resultsText: string;
    minRead: string;
    warningText: string;
    disturbingText: string;
    viewerText: string;
    stopText: string;
    testCalibrationText: string;
    automaticallyCalibrateText: string;
    switchFullScreenText: string;
    startText: string;
    errorConfig: IErrorConfig;
}

@Component({
    selector: 'lib-calibration',
    templateUrl: './calibration.component.html',
    styleUrls: ['./calibration.component.scss']
})
export class CalibrationComponent implements OnInit, OnDestroy {
    @Input() isEng = false;
    @Input() gazefilter: any;
    @Input() config: ICalibConfig = {
        calibrationText: 'Calibration',
        sec: 'sec',
        eyeTrackingTestText: 'Eye-tracking test',
        min: 'min',
        conventionalTestText: 'Conventional test',
        resultsText: 'Results',
        minRead: 'a 2 min read',
        warningText: 'Warning!',
        disturbingText: 'Disturbing and distressing images are used during the Anima test to draw conclusions about your well-being. These images may be unsuitable for some people.',
        viewerText: 'Viewer discretion is advised!',
        stopText: 'You can stop the test anytime by hitting the ESC button on the keyboard. You will not be charged for tests stopped prematurely.',
        testCalibrationText: 'Test Calibration',
        automaticallyCalibrateText: 'Circles will appear indiscriminately on the screen.<br>Your task is to point them with the cursor.<br>Calibration will be carried out automatically.',
        switchFullScreenText: 'Your browser will switch to full-screen mode for enhanced assessment accuracy.',
        startText: 'Start',
        errorConfig: {
            title: 'Oops!',
            text: 'Oops!',
            reload: 'Reload',
            tryLater: 'Try later'
        }
    };

    @Output() calibrated: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() calibrationStarted: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() errorEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Output() action: EventEmitter<any> = new EventEmitter<any>();

    @ViewChild('calibPoint') calibPoint: ElementRef;

    targetErrorValue = 0;

    errMsg: string | null = null;

    det = false;

    calibrationAmounts = 0;
    maxXalibrationAmounts = 30;

    private isCalibrationActive = false;


    private sub: Subscription;

    constructor(
        private service: EyeTrackerService
    ) {

    }

    ngOnInit(): void {
        let detectionEvents: any = [];

        this.sub = this.service.filter()
            .subscribe((res: any) => {
                if (!res) {
                    if (detectionEvents.length <= 10) {
                        detectionEvents.push(res);
                    }
                    if (detectionEvents.length === 10 && this.calibrationActive) {
                        this.detected = res;
                        this.errorEvent.emit(true);
                        this.sub.unsubscribe();
                    }
                } else {
                    detectionEvents = [];
                    this.detected = res;
                }
            });
    }

    ngOnDestroy() {
        if (this.calibrationActive) {
            this.gazefilter.tracker.removeListener('calib');
        }
    }

    get detected() {
        return this.det;
    }

    set detected(prop) {
        this.det = prop;
    }

    get calibrationActive() {
        return this.isCalibrationActive;
    }

    set calibrationActive(prop: boolean) {
        this.isCalibrationActive = prop;
    }

    onCalibrate(addCalibrationOnTest = false) {
        this.calibrationActive = true;
        this.service.addCalibrationOnTest = addCalibrationOnTest;
        this.service.fullScreen(true)
            .then(() => this.startCalibration())
            .then(() => {
                this.calibrationStarted.emit(true);
            })
            .catch((err: any) => {
                console.log(err);
                this.errorEvent.emit(true);
                // alert(`Error attempting to enable fullscreen mode: ${err.message} (${err.name})`);
            });
    }

    async startCalibration() {
        // this.calibrationActive = true;
        return await this.gazefilter.tracker.addListener('calib', this.onCalibration.bind(this));
    }

    setCalibrationNode(event: { timeStamp: number, screenX: number, screenY: number }) {
        this.calibrationAmounts++;
        if (this.calibrationAmounts === this.maxXalibrationAmounts) {
            this.service.fullScreen(false)
                .then(() => {
                    this.errorEvent.emit(true);
                });
        } else if (this.detected) {
            this.gazefilter.tracker.calibrate(
                event.timeStamp,
                event.screenX,
                event.screenY,
                1
            );
        }
    }

    onCalibration(response: any) {
        if (this.calibrationAmounts > 16 && !isNaN(response.errorValue) && response.errorValue > this.targetErrorValue) {
            this.calibrated.emit(true);
        }
    }
}
